import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { UrlService } from '@service/url/url.service';
import { ContactsTable, RestService, TruckSearchTable, IS_NOT_EMPTY } from 'cargo-chief-commons';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ACCOUNT_TYPE, COPY_TO_CLIPBOARD_MSGS } from '@domain/constants/app-variables';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

@Component({
	selector: 'app-contact-details-modal',
	templateUrl: './contact-details-modal.component.html',
	styleUrls: ['./contact-details-modal.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ContactDetailsModalComponent implements OnInit {
	@Input() text: string;
	@Input() contactsCount: number;
	@Input() carrierId: string;
	@Input() editPrimaryContact: boolean;
	@Input() showButton: boolean;
	@Input() segmentEvent: boolean;
	@Input() mc: string;
	@Input() dot: string;
	@Input() showCarrier: boolean;
	@Input() isCustomerCarrier: boolean = false;
	@Input() isFeedbackEnabled: boolean;
	@Input() carrierBusinessName: boolean;
	@Input() carrierHQ: string;
	@Input() isLane: boolean;
	@Input() lanePath: string;
	@Input() carrierLastFmcsaUpdate: string;
	@Input() drivers: string;
	@Input() powerUnits: string;
	@Input() phone: string;
	@Input() frequency: string;
	@Input() originDeadhead: string;
	@Input() destinationDeadhead: string;
	// this should be enabled from quote details page
	@Input() carrierBidContactId: string;
	// CN-8616: load + bid context (V2 bid list only) — when set, logging a call from a
	// contact's phone/email marks this bid Contacted; bidContacted re-surfaces it.
	@Input() logCallLoadId: string;
	@Input() logCallBidId: string;
	@Output() carrierSetup: EventEmitter<boolean> = new EventEmitter();
	@Output() bidContacted: EventEmitter<string> = new EventEmitter();

	constructor(public dialog: MatDialog) {}

	ngOnInit() {}

	showCarrierContactDetails(): void {
		const dialogRef = this.dialog.open(ContactDialogComponent, {
			width: '950px',
			data: {
				contactsCount: this.contactsCount,
				carrierId: this.carrierId,
				segmentEvent: this.segmentEvent,
				mc: this.mc,
				dot: this.dot,
				editPrimaryContact: this.editPrimaryContact,
				showCarrier: this.showCarrier,
				isFeedbackEnabled: this.isFeedbackEnabled,
				carrierBusinessName: this.carrierBusinessName,
				carrierHQ: this.carrierHQ,
				isLane: this.isLane,
				lanePath: this.lanePath,
				carrierLastFmcsaUpdate: this.carrierLastFmcsaUpdate,
				drivers: this.drivers,
				powerUnits: this.powerUnits,
				phone: this.phone,
				isCustomerCarrier: this.isCustomerCarrier,
				frequency: this.frequency,
				originDeadhead: this.originDeadhead,
				destinationDeadhead: this.destinationDeadhead,
				carrierBidContactId: this.carrierBidContactId,
				logCallLoadId: this.logCallLoadId,
				logCallBidId: this.logCallBidId,
			},
			disableClose: true,
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result == null) {
				return;
			}
			// CN-8616: the dialog now closes with an object so it can also report a
			// logged-call's Contacted bid. Keep back-compat with the legacy bare
			// boolean (isCustomerCarrier).
			if (typeof result === 'boolean') {
				this.carrierSetup.emit(result);
				return;
			}
			if (result.isCustomerCarrier != null) {
				this.carrierSetup.emit(result.isCustomerCarrier);
			}
			if (result.contactedBidId) {
				this.bidContacted.emit(result.contactedBidId);
			}
		});
	}
}

@Component({
	selector: 'contact-details-dialog',
	templateUrl: 'modal.template.html',
})
export class ContactDialogComponent implements OnInit {
	USER_ACCOUNT_TYPE = null;
	ACCOUNT_TYPE = ACCOUNT_TYPE;
	COPY_TO_CLIPBOARD_MSGS = COPY_TO_CLIPBOARD_MSGS;
	isCustomerCarrierUpdated = false;
	contactList: ContactsTable[] = null;
	capacitySearch = {} as TruckSearchTable;
	userResponseSegmentPayload = null;
	// CN-8616: bid id marked Contacted by a call logged from a contact's phone/email.
	contactedBidId: string = null;

	constructor(
		public dialogRef: MatDialogRef<ContactDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		public accountSettings: AccountSettingsService
	) {}

	ngOnInit() {
		this.USER_ACCOUNT_TYPE = this.cargochief.USER_ACCOUNT_TYPE;
		this.getContactsList();
		this.hydrateMissingCarrierMetadata();
		this.capacitySearch.carrierId = this.data.carrierId;
		this.capacitySearch.customerCarrier = this.data.isCustomerCarrier;
	}

	/**
	 * CN-8504: when callers don't pass a fully-hydrated row (e.g. Offer
	 * Analytics, or even Offers when the row is slim), fetch the carrier's
	 * canonical FMCSA-style fields by id and fill in any blanks. Existing
	 * caller-supplied values take precedence — we only set fields that are
	 * null/undefined. Lane-context fields (frequency, originDeadhead,
	 * destinationDeadhead) are intentionally NOT hydrated here — they only
	 * make sense in a search-context call and have no carrier-record source.
	 *
	 * Fails silently: contacts list path is independent and has its own
	 * error handling; if the carrier-fetch fails the modal just keeps
	 * showing blanks (the prior behavior).
	 *
	 * Demo-override caveat: `/carriers/-/ids` is hard-scoped to the
	 * authenticated user's account network. When a demo account (e.g. 5876)
	 * views Offer Analytics for an override account (e.g. 7829 via
	 * DEMO_ACCOUNT_OVERRIDE.ACCOUNT_OVERRIDES — CN-9038), the analytics rows reference
	 * carrier_ids that live in the override account's network — the lookup
	 * comes back empty and the modal stays blank. This is correct behavior
	 * for production brokers (they have those carriers in their own network),
	 * just an artifact of the demo-override architecture. If we ever need
	 * the demo case to hydrate too, it'd be a c4-server-side change to make
	 * the endpoint accept an override accountId.
	 */
	hydrateMissingCarrierMetadata() {
		const carrierId = this.data?.carrierId;
		if (!carrierId) {
			return;
		}
		const url = this.urlService.getCarriersByIds();
		this.restService.POST(url, { carrierIDs: [carrierId] }).subscribe(
			(result: any) => {
				const list = result?.data?.content || result?.data || [];
				const carrier = Array.isArray(list) ? list[0] : list;
				if (!carrier) {
					return;
				}
				// Only fill fields that aren't already provided by the caller.
				const fillIfBlank = (key: string, value: any) => {
					if (this.utilService.isNullOrUndefined(this.data[key]) && !this.utilService.isNullOrUndefined(value)) {
						this.data[key] = value;
					}
				};
				fillIfBlank('drivers', carrier.drivers);
				fillIfBlank('powerUnits', carrier.powerUnits);
				fillIfBlank('phone', carrier.phone);
				fillIfBlank('carrierHQ', carrier.carrierHQ ?? carrier.hq);
				fillIfBlank('mc', carrier.mc);
				fillIfBlank('dot', carrier.dot);
				fillIfBlank('carrierBusinessName', carrier.businessName);
				fillIfBlank('carrierLastFmcsaUpdate', carrier.lastFmcsaUpdate ?? carrier.carrierLastFmcsaUpdate);
			},
			() => {
				// Silent — leave the fields blank as before. Contacts list path
				// is independent and shows its own snackbar on its own failure.
			}
		);
	}

	getContactsList() {
		this.contactList = null;
		const contactsUrl = this.urlService.contactsUrl(this.data['carrierId']);
		this.restService.GET(contactsUrl).subscribe(
			(result) => {
				result['carrier'] = this.getCarrierInfo();
				this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.contact_requested, result);
				if (!IS_NOT_EMPTY(this.data?.logCallBidId)) {
					this.markSingleBidContact(result['data']);
				}
				this.contactList = result['data'];
			},
			(error) => {
				this.utilService.showSnackbar(
					'Cannot fetch contact list.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.contactList = [];
			}
		);
	}

	// CN-8616: record the bid a logged call marked Contacted so it's reported when the
	// modal closes.
	onContactLogged(bidId: string): void {
		this.contactedBidId = bidId;
	}

	closeDialog(): void {
		// CN-8616: close with an object so the wrapper can emit both the
		// customer-carrier update and any logged-call Contacted bid. Falls back to the
		// legacy null close when neither happened.
		const isCustomerCarrier = this.isCustomerCarrierUpdated ? this.data.isCustomerCarrier : null;

		if (isCustomerCarrier == null && !this.contactedBidId) {
			this.dialogRef.close(null);

			return;
		}

		this.dialogRef.close({ isCustomerCarrier, contactedBidId: this.contactedBidId });
	}

	allQuestionsAnswered(payload: any) {
		if (!this.utilService.isNullOrUndefined(payload)) {
			this.userResponseSegmentPayload = payload;
		} else {
			this.userResponseSegmentPayload = payload;
		}
	}

	submitFeedback() {
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_submitted_carrier_feedback,
			this.userResponseSegmentPayload
		);
		this.closeDialog();
	}

	getCarrierInfo() {
		return {
			businessName: this.data.carrierBusinessName,
			mc: this.data.mc,
			dot: this.data.dot,
			carrierId: this.data.carrierId,
		};
	}

	markSingleBidContact(contacts) {
		const contactId = this.data?.carrierBidContactId;
		if (IS_NOT_EMPTY(contactId)) {
			const matchedCount: number = contacts.filter(
				(contact) => contactId === contact.contactId || contactId === contact.id
			).length;
			if (matchedCount === 1) {
				contacts.forEach((contact) => {
					contact.primaryContact = contactId === contact.contactId || contactId === contact.id;
				});
			}
		}
	}

}
